import React, { useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import { Field } from '@campfire/field';
import { Form, Formik } from 'formik';
import { EditorState, convertToRaw, convertFromRaw } from 'draft-js';
import { TabletButton } from '@campfire/tablet-button';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { FileUploadGallery, FugFile } from '@campfire/file-upload-gallery';
import { FileCopy } from '@material-ui/icons';
import { FullscreenDialog } from '../../../../../common/dialogs/FullscreenDialog';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { FUNNEL_FORM_OPTIONS_QUERY } from './admin-console-funnel-options.gql';
import { AdminConsoleFunnel } from './AdminConsoleFunnels';

interface Props {
  funnel?: AdminConsoleFunnel;
  duplicateFrom?: AdminConsoleFunnel;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError?: (message: string) => void;
}

interface FunnelFormValues {
  funnelId?: string;
  name: string;
  slug: string;
  description: EditorState;
  expiresAt: string;
  lockPrograms: boolean;
  lockActivities: boolean;
  lockRoles: boolean;
  showTaskList: boolean;
  programIds: string[];
  activityIds: string[];
  activityRoleIds: string[];
  imageAttachments: FugFile[];
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function parseDescription(descriptionJson: string | null): EditorState {
  if (!descriptionJson) return EditorState.createEmpty();
  try {
    return EditorState.createWithContent(convertFromRaw(JSON.parse(descriptionJson)));
  } catch {
    return EditorState.createEmpty();
  }
}

const useUpdateFunnelImageFetch = () => {
  return useEndpointFetch<{ funnelId: string; image: FugFile }>('/vm/funnel/image/update', {
    formData: true,
  });
};

export function FormFunnelDialog({ funnel, duplicateFrom, onClose, onSuccess, onError }: Props) {
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });
  const updateFunnelImage = useUpdateFunnelImageFetch();
  const { data: optionsData } = useCampfireQuery<any, {}>(FUNNEL_FORM_OPTIONS_QUERY, {});
  const [submitError, setSubmitError] = React.useState<string>();

  const programs = optionsData?.vm?.programs ?? [];
  const allActivities = useMemo(
    () => programs.flatMap((p: any) => (p.activities ?? []).map((a: any) => ({ ...a, programId: p.programId }))),
    [programs]
  );
  const activityRoles = optionsData?.vm?.activityRoles ?? [];

  const source = funnel || duplicateFrom;
  const isEditing = Boolean(funnel?.funnelId);

  const existingImageFiles = useMemo(
    () => (source?.image ? [{ fileId: source.image, url: source.image, name: source.image }] : []),
    [source?.image]
  );
  const handleFilesAddedRef = useRef((_unusedFiles: FugFile[]) => {});
  const handleFileRemovedRef = useRef(() => {});

  const initialValues: FunnelFormValues = useMemo(
    () => ({
      funnelId: funnel?.funnelId,
      name: duplicateFrom ? `Copy of ${duplicateFrom.name}` : source?.name ?? '',
      slug: duplicateFrom ? '' : source?.slug ?? '',
      description: parseDescription(source?.description ?? null),
      expiresAt: source?.expiresAt ? source.expiresAt.substring(0, 16) : '',
      lockPrograms: source?.lockPrograms ?? false,
      lockActivities: source?.lockActivities ?? false,
      lockRoles: source?.lockRoles ?? false,
      showTaskList: source?.showTaskList ?? true,
      programIds: source?.programs?.map((p) => p.programId) ?? [],
      activityIds: source?.activities?.map((a) => a.activityId) ?? [],
      activityRoleIds: source?.activityRoles?.map((r) => r.activityRoleId) ?? [],
      imageAttachments: [],
    }),
    [funnel, duplicateFrom]
  );

  const handleSubmit = (vals: FunnelFormValues) => {
    const descriptionContent = vals.description.getCurrentContent();
    const hasDescription = descriptionContent.hasText();

    const data: any = {
      name: vals.name,
      slug: vals.slug,
      description: hasDescription ? JSON.stringify(convertToRaw(descriptionContent)) : null,
      expiresAt: vals.expiresAt || null,
      lockPrograms: vals.lockPrograms,
      lockActivities: vals.lockActivities,
      lockRoles: vals.lockRoles,
      showTaskList: vals.showTaskList,
      programIds: vals.programIds,
      activityIds: vals.activityIds,
      activityRoleIds: vals.activityRoleIds,
    };

    if (isEditing && funnel?.funnelId) {
      data.funnelId = funnel.funnelId;
    }

    setSubmitError(undefined);
    campfireMutation
      .run({
        url: `${apiUrl}/vm/funnel/save`,
        method: 'post',
        data,
      })
      .then((res: any) => {
        if (!res.ok) {
          const message = res?.data?.message || 'Failed to save funnel';
          setSubmitError(message);
          if (onError) onError(message);
          return;
        }

        const savedFunnelId = isEditing ? funnel?.funnelId : res?.data?.data?.funnelId;
        const hasNewImage = vals.imageAttachments.length > 0;

        if (savedFunnelId && hasNewImage) {
          updateFunnelImage
            .run({ funnelId: savedFunnelId, image: vals.imageAttachments[0] })
            .then(() => {
              onSuccess(isEditing ? 'Funnel updated!' : 'Funnel created!');
            })
            .catch(() => {
              onSuccess(isEditing ? 'Funnel updated (image upload failed)' : 'Funnel created (image upload failed)');
            });
        } else {
          onSuccess(isEditing ? 'Funnel updated!' : 'Funnel created!');
        }
      })
      .catch((error: any) => {
        const message = error?.response?.data?.message || 'Failed to save funnel';
        setSubmitError(message);
        if (onError) onError(message);
      });
  };

  const copyLink = (slug: string) => {
    const link = `${window.location.origin}/apply/${slug}`;
    navigator.clipboard.writeText(link);
  };

  return (
    <FullscreenDialog
      open
      close={onClose}
      title={isEditing ? 'Edit Funnel' : duplicateFrom ? 'Duplicate Funnel' : 'New Funnel'}
      maxWidth='md'
      fullWidth
    >
      <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
        {({ values, setFieldValue }) => {
          handleFilesAddedRef.current = (files: FugFile[]) => setFieldValue('imageAttachments', files);
          handleFileRemovedRef.current = () => setFieldValue('imageAttachments', []);
          return (
            <Form>
              <Box mt='20px'>
                <Field
                  variant='outlined'
                  label='Funnel Name'
                  name='name'
                  required
                  autoFocus
                  fullWidth
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('name', e.target.value);
                    if (!isEditing) {
                      setFieldValue('slug', toSlug(e.target.value));
                    }
                  }}
                />

                <Box marginTop='1.25rem'>
                  <Field
                    variant='outlined'
                    label='Slug'
                    name='slug'
                    required
                    fullWidth
                    helperText={values.slug ? `${window.location.origin}/apply/${values.slug}` : ''}
                    InputProps={{
                      endAdornment: values.slug ? (
                        <InputAdornment position='end'>
                          <IconButton size='small' onClick={() => copyLink(values.slug)}>
                            <FileCopy fontSize='small' />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Box>

                <Box marginTop='1.25rem'>
                  <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '8px' }}>
                    DESCRIPTION
                  </Typography>
                  <CampfireRichEditor
                    placeholder='Add a description for this funnel (shown to applicants)...'
                    editorState={values.description}
                    setEditorState={(eState) => {
                      setFieldValue('description', eState);
                    }}
                  />
                </Box>

                <Box marginTop='1.25rem'>
                  <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '8px' }}>
                    BANNER IMAGE
                  </Typography>
                  <Typography style={{ fontSize: '12px', color: '#808080', paddingBottom: '8px' }}>
                    Upload an image to display on the funnel application page.
                  </Typography>
                  <FileUploadGallery
                    id='funnelImage'
                    allowMultiple={false}
                    accept='image/*'
                    uploadedFiles={existingImageFiles}
                    handleFilesAdded={(files: FugFile[]) => handleFilesAddedRef.current(files)}
                    handleFileRemoved={() => handleFileRemovedRef.current()}
                  />
                </Box>

                <Box marginTop='1.25rem'>
                  <Field
                    variant='outlined'
                    label='Expires At'
                    name='expiresAt'
                    type='datetime-local'
                    fullWidth
                    shrink
                  />
                </Box>

                <Box marginTop='1.5rem'>
                  <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '12px' }}>
                    PROGRAMS
                  </Typography>
                  <FormControl fullWidth variant='outlined'>
                    <InputLabel variant='outlined' shrink>
                      Select programs
                    </InputLabel>
                    <Select
                      multiple
                      value={values.programIds}
                      onChange={(e) => {
                        const newProgramIds = e.target.value as string[];
                        setFieldValue('programIds', newProgramIds);
                        const validActivityIds = allActivities
                          .filter((a: any) => newProgramIds.includes(a.programId))
                          .map((a: any) => a.activityId);
                        setFieldValue(
                          'activityIds',
                          values.activityIds.filter((id) => validActivityIds.includes(id))
                        );
                      }}
                      input={<OutlinedInput notched label='Select programs' />}
                      renderValue={(selected) => {
                        const ids = selected as string[];
                        if (ids.length === 0) return 'No programs selected';
                        return programs
                          .filter((p: any) => ids.includes(p.programId))
                          .map((p: any) => p.name)
                          .join(', ');
                      }}
                    >
                      {programs.map((program: any) => (
                        <MenuItem key={program.programId} value={program.programId}>
                          <ListItemIcon>
                            <Checkbox color='primary' checked={values.programIds.indexOf(program.programId) > -1} />
                          </ListItemIcon>
                          <ListItemText primary={program.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box marginTop='1.25rem'>
                  <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '12px' }}>
                    ACTIVITIES
                  </Typography>
                  <FormControl fullWidth variant='outlined'>
                    <InputLabel variant='outlined' shrink>
                      {values.programIds.length === 0 ? 'Select programs first' : 'Select activities'}
                    </InputLabel>
                    <Select
                      multiple
                      disabled={values.programIds.length === 0}
                      value={values.activityIds}
                      onChange={(e) => setFieldValue('activityIds', e.target.value)}
                      input={
                        <OutlinedInput
                          notched
                          label={values.programIds.length === 0 ? 'Select programs first' : 'Select activities'}
                        />
                      }
                      renderValue={(selected) => {
                        const ids = selected as string[];
                        const filteredActivities = allActivities.filter((a: any) =>
                          values.programIds.includes(a.programId)
                        );
                        if (ids.length === 0) return 'No activities selected';
                        if (ids.length === filteredActivities.length) return 'All activities';
                        return allActivities
                          .filter((a: any) => ids.includes(a.activityId))
                          .map((a: any) => a.name)
                          .join(', ');
                      }}
                    >
                      {allActivities
                        .filter((a: any) => values.programIds.includes(a.programId))
                        .map((activity: any) => (
                          <MenuItem key={activity.activityId} value={activity.activityId}>
                            <ListItemIcon>
                              <Checkbox
                                color='primary'
                                checked={values.activityIds.indexOf(activity.activityId) > -1}
                              />
                            </ListItemIcon>
                            <ListItemText primary={activity.name} />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box marginTop='1.25rem'>
                  <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '12px' }}>
                    ROLES
                  </Typography>
                  <FormControl fullWidth variant='outlined'>
                    <InputLabel variant='outlined' shrink>
                      Select roles
                    </InputLabel>
                    <Select
                      multiple
                      value={values.activityRoleIds}
                      onChange={(e) => setFieldValue('activityRoleIds', e.target.value)}
                      input={<OutlinedInput notched label='Select roles' />}
                      renderValue={(selected) => {
                        const ids = selected as string[];
                        if (ids.length === 0) return 'All roles';
                        return activityRoles
                          .filter((r: any) => ids.includes(r.activityRoleId))
                          .map((r: any) => r.name)
                          .join(', ');
                      }}
                    >
                      {activityRoles.map((role: any) => (
                        <MenuItem key={role.activityRoleId} value={role.activityRoleId}>
                          <ListItemIcon>
                            <Checkbox
                              color='primary'
                              checked={values.activityRoleIds.indexOf(role.activityRoleId) > -1}
                            />
                          </ListItemIcon>
                          <ListItemText primary={role.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box marginTop='1.5rem'>
                  <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '8px' }}>
                    DISPLAY SETTINGS
                  </Typography>

                  <Box display='flex' mt='5px'>
                    <Switch
                      checked={values.showTaskList}
                      onChange={() => setFieldValue('showTaskList', !values.showTaskList)}
                      color='primary'
                    />
                    <Box>
                      <Typography
                        style={{ fontSize: '13px', color: '#000000b0', fontWeight: 500, textTransform: 'uppercase' }}
                      >
                        Show Summary
                      </Typography>
                      <Typography style={{ fontSize: '12px', color: '#808080' }}>
                        Display a summary of tasks associated with the selected programs, activities, and roles on the
                        funnel page.
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box marginTop='1.5rem'>
                  <Typography style={{ fontSize: '12px', fontWeight: 500, color: '#000000b0', paddingBottom: '8px' }}>
                    LOCK SETTINGS
                  </Typography>
                  <Typography style={{ fontSize: '12px', color: '#808080', paddingBottom: '8px' }}>
                    When locked, applicants using this funnel cannot change the pre-selected options.
                  </Typography>

                  <Box display='flex' mt='5px'>
                    <Switch
                      checked={values.lockPrograms}
                      onChange={() => setFieldValue('lockPrograms', !values.lockPrograms)}
                      color='primary'
                    />
                    <Box>
                      <Typography
                        style={{ fontSize: '13px', color: '#000000b0', fontWeight: 500, textTransform: 'uppercase' }}
                      >
                        Lock Programs
                      </Typography>
                      <Typography style={{ fontSize: '12px', color: '#808080' }}>
                        Applicants cannot change the pre-selected programs.
                      </Typography>
                    </Box>
                  </Box>

                  <Box display='flex' mt='5px'>
                    <Switch
                      checked={values.lockActivities}
                      onChange={() => setFieldValue('lockActivities', !values.lockActivities)}
                      color='primary'
                    />
                    <Box>
                      <Typography
                        style={{ fontSize: '13px', color: '#000000b0', fontWeight: 500, textTransform: 'uppercase' }}
                      >
                        Lock Activities
                      </Typography>
                      <Typography style={{ fontSize: '12px', color: '#808080' }}>
                        Applicants cannot change the pre-selected activities.
                      </Typography>
                    </Box>
                  </Box>

                  <Box display='flex' mt='5px'>
                    <Switch
                      checked={values.lockRoles}
                      onChange={() => setFieldValue('lockRoles', !values.lockRoles)}
                      color='primary'
                    />
                    <Box>
                      <Typography
                        style={{ fontSize: '13px', color: '#000000b0', fontWeight: 500, textTransform: 'uppercase' }}
                      >
                        Lock Roles
                      </Typography>
                      <Typography style={{ fontSize: '12px', color: '#808080' }}>
                        Applicants cannot change the pre-selected roles.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {submitError && (
                <Box mt='1rem' p='12px' borderRadius='4px' style={{ backgroundColor: '#ffebee' }}>
                  <Typography style={{ color: '#c62828', fontSize: '13px' }}>{submitError}</Typography>
                </Box>
              )}

              <Box display='flex' marginTop='1.25rem' width='100%' justifyContent='flex-end'>
                <TabletButton onClick={onClose} style={{ marginRight: '8px' }}>
                  Cancel
                </TabletButton>
                <TabletButton variant='contained' type='submit' color='primary'>
                  {isEditing ? 'Save' : 'Create'}
                </TabletButton>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </FullscreenDialog>
  );
}
